import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  Select,
  MenuItem,
  Box,
  Alert,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import AppDialog, { DialogField } from '../common/AppDialog';
import { cadenceLabel } from '../../utils/generateOccurrences';

const ActionFormDialog = ({
  open,
  onClose,
  editAction,
  accounts,
  categories = [],
  onSubmit,
  loading,
  formError
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    amount: '',
    type: 'expense',
    categoryId: '',
    sourceAccountId: '',
    destinationAccountId: '',
    startDate: new Date(), // Default to today
    endDate: null,
    frequency: 'once',
    interval: 1,
    dayOfMonth: '',
    dayOfWeek: '',
    isActive: true,
    notes: ''
  });

  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (open) {
      if (editAction) {
        setFormData({
          name: editAction.name || '',
          description: editAction.description || '',
          amount: editAction.amount || '',
          type: editAction.type || 'expense',
          categoryId: editAction.categoryId || '',
          sourceAccountId: editAction.sourceAccountId || '',
          destinationAccountId: editAction.destinationAccountId || '',
          startDate: editAction.startDate ? new Date(editAction.startDate) : null,
          endDate: editAction.endDate ? new Date(editAction.endDate) : null,
          frequency: editAction.frequency || 'once',
          interval: editAction.interval || 1,
          dayOfMonth: editAction.dayOfMonth || '',
          dayOfWeek: editAction.dayOfWeek || '',
          isActive: editAction.isActive !== undefined ? editAction.isActive : true,
          notes: editAction.notes || ''
        });
      } else {
        setFormData({
          name: '',
          description: '',
          amount: '',
          type: 'expense',
          categoryId: '',
          sourceAccountId: '',
          destinationAccountId: '',
          startDate: new Date(), // Default to today
          endDate: null,
          frequency: 'once',
          interval: 1,
          dayOfMonth: '',
          dayOfWeek: '',
          isActive: true,
          notes: ''
        });
      }
    }
  }, [open, editAction]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDateChange = (name, date) => {
    setFormData(prev => ({ ...prev, [name]: date }));
  };

  const handleSubmit = async () => {
    setSubmitError('');

    const errors = [];
    if (!formData.name?.trim()) errors.push('Name is required');
    if (formData.amount === '' || formData.amount === null || Number.isNaN(Number(formData.amount))) errors.push('Amount is required');
    if (!formData.sourceAccountId) errors.push('Source account is required');
    if (!formData.startDate) errors.push('Start date is required');
    if (formData.type === 'transfer' && !formData.destinationAccountId) errors.push('Destination account is required for transfers');

    if (errors.length) {
      setSubmitError(errors.join('. '));
      return;
    }

    const amount = Number(formData.amount);
    if (!Number.isFinite(amount)) {
      setSubmitError('Amount must be a valid number');
      return;
    }

    // Normalize dates to ISO strings so server reliably parses as Date
    const normalizeDate = (d) => (d instanceof Date ? d.toISOString() : d);

    // Only send dayOfMonth/dayOfWeek when relevant
    const dayOfMonth = formData.frequency === 'monthly' && formData.dayOfMonth !== ''
      ? parseInt(formData.dayOfMonth, 10)
      : undefined;
    const dayOfWeek = formData.frequency === 'weekly' && formData.dayOfWeek !== ''
      ? parseInt(formData.dayOfWeek, 10)
      : undefined;

    // Interval only matters for recurring schedules; transfers sit outside
    // budget math, so they never carry a category.
    const interval = formData.frequency !== 'once' && Number.isFinite(parseInt(formData.interval, 10))
      ? Math.max(1, parseInt(formData.interval, 10))
      : 1;

    const actionData = {
      ...formData,
      amount,
      categoryId: formData.type === 'transfer' ? '' : formData.categoryId,
      startDate: normalizeDate(formData.startDate),
      endDate: formData.endDate ? normalizeDate(formData.endDate) : undefined,
      interval,
      dayOfMonth,
      dayOfWeek,
    };

    // Don’t let client-generated IDs collide with server uniqueness.
    delete actionData.id;

    await onSubmit(actionData);
  };

  const getAccountName = (accountId) => {
    const account = accounts.find(a => a.id === accountId);
    return account ? account.name : 'Unknown Account';
  };

  const getTypeColor = (type) => {
    const colors = {
      expense: 'error.main',
      income: 'success.main',
      transfer: 'warning.main',
    };
    return colors[type] || 'text.secondary';
  };

  // Selected account name, clamped so long names don't blow out a narrow select.
  const renderAccountValue = (selected) =>
    selected ? (
      <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {getAccountName(selected)}
      </Box>
    ) : (
      <Box component="span" sx={{ color: 'text.secondary' }}>Select account</Box>
    );

  const toDateInput = (d) =>
    d ? (typeof d === 'string' ? d : d.toISOString().slice(0, 10)) : '';

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={editAction ? 'Edit Action' : 'Add Action'}
      subtitle="Schedule money in, out, or between accounts"
      primaryLabel={loading ? (editAction ? 'Saving…' : 'Adding…') : (editAction ? 'Save' : 'Add')}
      onPrimary={handleSubmit}
      primaryDisabled={loading}
    >
      <DialogField label="Action Name" required>
        <TextField
          fullWidth
          size="small"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g. Transfer to savings"
        />
      </DialogField>

      {/* Type + Amount share a row — together they read as "what kind, how much". */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <DialogField label="Type" required sx={{ mt: 0, flex: 1, minWidth: 0 }}>
          <FormControl fullWidth size="small">
            <Select
              name="type"
              value={formData.type}
              onChange={handleChange}
              renderValue={selected => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getTypeColor(selected) }} />
                  {selected.charAt(0).toUpperCase() + selected.slice(1)}
                </Box>
              )}
            >
              <MenuItem value="expense">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                  Expense
                </Box>
              </MenuItem>
              <MenuItem value="income">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                  Income
                </Box>
              </MenuItem>
              <MenuItem value="transfer">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
                  Transfer
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </DialogField>

        <DialogField label="Amount" required sx={{ mt: 0, flex: 1, minWidth: 0 }}>
          <TextField
            fullWidth
            size="small"
            name="amount"
            type="number"
            value={formData.amount}
            onChange={handleChange}
            InputProps={{
              startAdornment: <Box sx={{ color: 'text.secondary', pr: 1 }}>$</Box>,
              inputProps: { min: 0 }
            }}
          />
        </DialogField>
      </Box>

      <DialogField label="Description">
        <TextField
          fullWidth
          size="small"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Optional details about this action"
        />
      </DialogField>

      <DialogField label={formData.type === 'transfer' ? 'From Account' : 'Account'} required>
        <FormControl fullWidth size="small">
          <Select
            name="sourceAccountId"
            value={formData.sourceAccountId}
            onChange={handleChange}
            displayEmpty
            renderValue={renderAccountValue}
          >
            <MenuItem value="" sx={{ color: 'text.secondary' }}>Select account</MenuItem>
            {accounts.map(account => (
              <MenuItem key={account.id} value={account.id}>{account.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogField>

      {/* Destination only matters for transfers, so only show it then. */}
      {formData.type === 'transfer' && (
        <DialogField label="To Account" required>
          <FormControl fullWidth size="small">
            <Select
              name="destinationAccountId"
              value={formData.destinationAccountId}
              onChange={handleChange}
              displayEmpty
              renderValue={renderAccountValue}
            >
              <MenuItem value="" sx={{ color: 'text.secondary' }}>Select account</MenuItem>
              {accounts.map(account => (
                <MenuItem key={account.id} value={account.id} disabled={account.id === formData.sourceAccountId}>
                  {account.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogField>
      )}

      {/* Transfers sit outside budget math, so only income/expense get a category. */}
      {formData.type !== 'transfer' && (
        <DialogField label="Category">
          <FormControl fullWidth size="small">
            <Select
              name="categoryId"
              value={formData.categoryId}
              onChange={handleChange}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return <Box component="span" sx={{ color: 'text.secondary' }}>Uncategorized</Box>;
                const cat = categories.find(c => c.id === selected);
                return cat ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: cat.color || '#9e9e9e', mr: 1 }} />
                    {cat.name}
                  </Box>
                ) : null;
              }}
            >
              <MenuItem value="">
                <Box component="span" sx={{ color: 'text.secondary' }}>Uncategorized</Box>
              </MenuItem>
              {categories.filter(cat => !cat.isTransfer).map(category => (
                <MenuItem key={category.id} value={category.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: category.color || '#9e9e9e', mr: 1 }} />
                    {category.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogField>
      )}

      {/* Frequency + interval read together as one cadence, e.g. "Weekly, every 2" = fortnightly. */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <DialogField label="Frequency" required sx={{ mt: 0, flex: 1, minWidth: 0 }}>
          <FormControl fullWidth size="small">
            <Select name="frequency" value={formData.frequency} onChange={handleChange}>
              <MenuItem value="once">One-time</MenuItem>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </Select>
          </FormControl>
        </DialogField>
        {formData.frequency !== 'once' && (
          <DialogField label="Repeat Every" sx={{ mt: 0, flex: 1, minWidth: 0 }}>
            <TextField
              fullWidth
              size="small"
              name="interval"
              type="number"
              value={formData.interval}
              onChange={handleChange}
              helperText={cadenceLabel(formData.frequency, formData.interval)}
              InputProps={{
                inputProps: { min: 1, max: 365 },
                endAdornment: (
                  <Box sx={{ color: 'text.secondary', pl: 1, whiteSpace: 'nowrap' }}>
                    {{ daily: 'days', weekly: 'weeks', monthly: 'months', yearly: 'years' }[formData.frequency]}
                  </Box>
                )
              }}
            />
          </DialogField>
        )}
      </Box>

      {formData.frequency === 'monthly' && (
        <DialogField label="Day of Month">
          <TextField
            fullWidth
            size="small"
            name="dayOfMonth"
            type="number"
            value={formData.dayOfMonth}
            onChange={handleChange}
            placeholder="1–31"
            InputProps={{ inputProps: { min: 1, max: 31 } }}
          />
        </DialogField>
      )}

      {formData.frequency === 'weekly' && (
        <DialogField label="Day of Week">
          <FormControl fullWidth size="small">
            <Select
              name="dayOfWeek"
              value={formData.dayOfWeek}
              onChange={handleChange}
              displayEmpty
              renderValue={selected =>
                (selected === '' || selected === undefined)
                  ? <Box component="span" sx={{ color: 'text.secondary' }}>Select day</Box>
                  : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][selected]
              }
            >
              <MenuItem value="" sx={{ color: 'text.secondary' }}>Select day</MenuItem>
              <MenuItem value={0}>Sunday</MenuItem>
              <MenuItem value={1}>Monday</MenuItem>
              <MenuItem value={2}>Tuesday</MenuItem>
              <MenuItem value={3}>Wednesday</MenuItem>
              <MenuItem value={4}>Thursday</MenuItem>
              <MenuItem value={5}>Friday</MenuItem>
              <MenuItem value={6}>Saturday</MenuItem>
            </Select>
          </FormControl>
        </DialogField>
      )}

      {/* Start + End share a row; End is irrelevant for one-time actions. */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <DialogField label="Start Date" required sx={{ mt: 0, flex: 1, minWidth: 0 }}>
          <TextField
            fullWidth
            size="small"
            type="date"
            value={toDateInput(formData.startDate)}
            onChange={e => handleDateChange('startDate', e.target.value ? new Date(e.target.value) : null)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogField>
        <DialogField label="End Date" sx={{ mt: 0, flex: 1, minWidth: 0 }}>
          <TextField
            fullWidth
            size="small"
            type="date"
            value={toDateInput(formData.endDate)}
            onChange={e => handleDateChange('endDate', e.target.value ? new Date(e.target.value) : null)}
            InputLabelProps={{ shrink: true }}
            disabled={formData.frequency === 'once'}
          />
        </DialogField>
      </Box>

      <DialogField label="Notes">
        <TextField
          fullWidth
          size="small"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          multiline
          rows={3}
          placeholder="Add any additional notes..."
        />
      </DialogField>

      <DialogField>
        <FormControlLabel
          control={<Checkbox checked={formData.isActive} onChange={handleChange} name="isActive" />}
          label="Active"
        />
      </DialogField>

      {(submitError || formError) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {submitError || formError}
        </Alert>
      )}
    </AppDialog>
  );
};

export default ActionFormDialog;
